<script lang="ts">
    import { onMount } from 'svelte/internal'
    import Zinput from '../../_common/z/zinput.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { faLink, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
    import { SendMessageTemplate } from '../../_common/send-message.template'
    import type { FormV2Form } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'

    export let formV2FormId: string
    export let completed: boolean = false

    const SIGNING = 105
    const SIGNED = 110
    const MODE_LINK = 1
    const MODE_EMAIL = 2

    const expirationOptions = [
        { label: '1 hora', value: 1 / 24 },
        { label: '1 dia', value: 1 },
        { label: '3 dias', value: 3 },
        { label: '10 dias', value: 10 },
        { label: '15 dias', value: 15 },
        { label: '1 mês', value: 30 },
        { label: '3 meses', value: 90 },
        { label: '1 ano', value: 365 },
        { label: '3 anos', value: 365 * 3 },
        { label: '5 anos', value: 365 * 5 },
    ]

    const modeOptions = [
        { label: 'Link de assinatura (assinatura simples)', value: MODE_LINK },
        { label: 'Assinatura por e-mail (assinatura avançada)', value: MODE_EMAIL },
    ]

    let show = false
    let form: FormV2Form | null = null
    let signatureRequested = false
    let signatureMode = MODE_LINK
    let savingSignature = false

    let expirationDays = 3
    let linkUrl = ''
    let linkRelativePath = ''
    let generating = false

    onMount(loadForm)

    async function loadForm() {
        try {
            show = true
            form = await window.api.tools.formsV2.forms.getOne(formV2FormId)
            // Vem do que foi configurado no template e congelado no snapshot na emissão. "requested"
            // é só leitura aqui (o modelo decide); a modalidade pode ser trocada para este documento.
            const signature = (form.document as any)?.templateSnapshot?.headerFooter?.signature
            signatureRequested = signature?.requested ?? false
            signatureMode = signature?.mode ?? MODE_LINK
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function compactToken(formV2FormLinkId: string, token: string) {
        return window.api.utils.conversions.uuidToBase58(formV2FormLinkId) + '_' + window.api.utils.conversions.uuidToBase58(token)
    }

    async function updateSignatureSetting() {
        if (!form) return
        try {
            savingSignature = true
            const document: any = form.document
            if (!document.templateSnapshot) document.templateSnapshot = { title: '', headerFooter: {}, blocks: [] }
            if (!document.templateSnapshot.headerFooter) document.templateSnapshot.headerFooter = {}
            // "requested" é reescrito com o mesmo valor que veio do snapshot — a tela nunca o altera.
            document.templateSnapshot.headerFooter.signature = { requested: signatureRequested, mode: signatureMode }
            await window.api.tools.formsV2.forms.update(formV2FormId, { document })
        } catch (error) {
            window.error(error)
        } finally {
            savingSignature = false
        }
    }

    async function generateLink() {
        try {
            generating = true
            linkUrl = ''
            linkRelativePath = ''
            const expiresAt = new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000)
            const link = await window.api.tools.formsV2.formLinks.create({ formV2FormId, expiresAt })
            linkRelativePath = 'public/forms-v2?id=' + compactToken(link.formV2FormLinkId, link.token)
            linkUrl = window.api.utils.utils.domain() + '/' + linkRelativePath
        } catch (error) {
            window.error(error)
        } finally {
            generating = false
        }
    }

    function sendMessage() {
        if (!linkRelativePath) return
        window.message(SendMessageTemplate.share_link_02(linkRelativePath, 'Link de preenchimento', form?.personId ?? null))
    }
</script>

<div class="root">
    <Zframe class="m-3">
        {#if completed}
            <div class="alert alert-warning mb-3">
                Documento concluído: o destinatário só poderá visualizar. Apenas documentos em "preenchimento" podem ser editados.
            </div>
        {/if}

        <Zinput type="select" label="Validade do link" options={expirationOptions} bind:value={expirationDays} />

        <!-- Pedir ou não assinatura é decisão do modelo ("Solicitar assinatura ao preencher", em
             Propriedades do template) e não pode ser ligada por documento: aqui só se escolhe a
             modalidade, e apenas quando o modelo já pede assinatura. -->
        {#if form && (form.status === SIGNING || form.status === SIGNED)}
            <div class="alert alert-info mt-3 mb-0">Documento em processo de assinatura: a modalidade não pode mais ser alterada.</div>
        {:else if signatureRequested}
            <Zinput
                class="mt-2"
                type="select"
                label="Modalidade de assinatura"
                options={modeOptions}
                bind:value={signatureMode}
                disabled={savingSignature}
                on:change={updateSignatureSetting}
            />
        {/if}

        <Zbutton class="mt-3" color="primary" icon={faLink} label="Gerar link" loading={generating} disabled={generating} on:click={generateLink} />
        {#if linkUrl}
            <div class="link-row mt-3">
                <div class="link-row_field">
                    <Zinput disabled={true} label="Link" value={linkUrl} showCopy={true} />
                </div>
                <Zbutton icon={faPaperPlane} color="secondary" label="Enviar" on:click={sendMessage} />
            </div>
        {/if}
    </Zframe>
    <LoadSpinner {show} />
</div>

<style>
    .root {
        position: relative;
        min-height: 120px;
    }
    .link-row {
        display: flex;
        align-items: flex-end;
        gap: 8px;
    }
    .link-row_field {
        flex: 1;
        min-width: 0;
    }
</style>
